import React, { useState } from 'react';
import Header from './Header';
import '../css/KnowledgeHubPage.css';

function KnowledgeHubPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedItems, setExpandedItems] = useState({});

  // Knowledge base data
  const knowledgeBase = {
    'Getting Started': [
      {
        id: 'gs1',
        title: 'Welcome to ExodiaUno',
        description: 'Your complete guide to getting started with our talent platform',
        content: 'Welcome to ExodiaUno! This guide will walk you through setting up your account, understanding the platform, and making your first hire. Start by completing your company profile and exploring our talent pool.',
        tags: ['setup', 'onboarding', 'first-steps'],
        difficulty: 'Beginner',
        estimatedTime: '5 min read'
      },
      {
        id: 'gs2',
        title: 'Account Setup & Verification',
        description: 'Complete your company profile and verify your account',
        content: 'Follow these steps to set up your account: 1) Complete company profile, 2) Upload required documents, 3) Verify KYC status, 4) Set up payment methods, 5) Configure team permissions.',
        tags: ['verification', 'kyc', 'profile'],
        difficulty: 'Beginner',
        estimatedTime: '10 min read'
      },
      {
        id: 'gs3',
        title: 'First Talent Search',
        description: 'Learn how to find and evaluate your first talent',
        content: 'Start your talent search by: 1) Defining role requirements, 2) Setting budget and timeline, 3) Using filters and search tools, 4) Reviewing candidate profiles, 5) Initiating contact.',
        tags: ['search', 'hiring', 'evaluation'],
        difficulty: 'Beginner',
        estimatedTime: '8 min read'
      }
    ],
    'Using the Portal': [
      {
        id: 'up1',
        title: 'Navigation & Dashboard',
        description: 'Master the portal interface and dashboard features',
        content: 'Learn to navigate the portal efficiently: Dashboard overview, menu navigation, quick actions, notifications, and user preferences. Customize your dashboard for optimal workflow.',
        tags: ['navigation', 'dashboard', 'interface'],
        difficulty: 'Beginner',
        estimatedTime: '6 min read'
      },
      {
        id: 'up2',
        title: 'Advanced Search & Filters',
        description: 'Use advanced search techniques to find perfect candidates',
        content: 'Master advanced search: Skill-based filtering, experience levels, availability, location preferences, portfolio review, and saved searches. Create custom search criteria.',
        tags: ['search', 'filters', 'advanced'],
        difficulty: 'Intermediate',
        estimatedTime: '12 min read'
      },
      {
        id: 'up3',
        title: 'Communication Tools',
        description: 'Effectively communicate with candidates and team members',
        content: 'Use our communication tools: In-app messaging, video calls, file sharing, collaboration spaces, and team coordination. Maintain professional communication throughout the hiring process.',
        tags: ['communication', 'messaging', 'collaboration'],
        difficulty: 'Intermediate',
        estimatedTime: '10 min read'
      }
    ],
    'Credits & Assessment': [
      {
        id: 'ca1',
        title: 'Understanding Credits',
        description: 'Learn how credits work and when to use them',
        content: 'Credits are your platform currency: 1 credit = 1 talent assessment, 1 role pass = 1 hiring slot, Credit usage for interviews, assessments, and evaluations, Credit management and purchasing.',
        tags: ['credits', 'currency', 'assessment'],
        difficulty: 'Beginner',
        estimatedTime: '7 min read'
      },
      {
        id: 'ca2',
        title: 'Role Pass Management',
        description: 'Manage your role passes and hiring slots effectively',
        content: 'Role passes allow you to: Create new job postings, Access talent pools, Conduct interviews, Make hiring decisions, Track usage and limits, Purchase additional passes.',
        tags: ['role-pass', 'hiring', 'slots'],
        difficulty: 'Intermediate',
        estimatedTime: '9 min read'
      },
      {
        id: 'ca3',
        title: 'Assessment Process',
        description: 'Complete guide to talent assessment and evaluation',
        content: 'Assessment workflow: 1) Initial screening, 2) Skills assessment, 3) Technical evaluation, 4) Cultural fit interview, 5) Reference checks, 6) Final decision making.',
        tags: ['assessment', 'evaluation', 'workflow'],
        difficulty: 'Intermediate',
        estimatedTime: '15 min read'
      }
    ],
    'Documents & Contracts': [
      {
        id: 'dc1',
        title: 'Contract Management',
        description: 'Manage all your hiring contracts and agreements',
        content: 'Contract management includes: Template selection, Customization options, Legal compliance, Digital signatures, Contract storage, Renewal management, and termination procedures.',
        tags: ['contracts', 'agreements', 'legal'],
        difficulty: 'Intermediate',
        estimatedTime: '11 min read'
      },
      {
        id: 'dc2',
        title: 'Document Upload & Storage',
        description: 'Securely upload and manage all required documents',
        content: 'Document management: Supported file formats, Security measures, Organization system, Version control, Access permissions, Backup and recovery, Compliance requirements.',
        tags: ['documents', 'upload', 'storage'],
        difficulty: 'Beginner',
        estimatedTime: '8 min read'
      },
      {
        id: 'dc3',
        title: 'Compliance & Legal',
        description: 'Ensure compliance with all legal and regulatory requirements',
        content: 'Compliance areas: Labor laws, Tax regulations, Immigration requirements, Data protection, Industry standards, Regular audits, Legal consultation services.',
        tags: ['compliance', 'legal', 'regulations'],
        difficulty: 'Advanced',
        estimatedTime: '20 min read'
      }
    ],
    'EOR & Buy-Out Guide': [
      {
        id: 'eb1',
        title: 'Employer of Record (EOR) Services',
        description: 'Understanding EOR services and benefits',
        content: 'EOR services provide: Payroll management, Tax compliance, Benefits administration, HR support, Legal compliance, Risk management, and seamless employee experience.',
        tags: ['eor', 'payroll', 'compliance'],
        difficulty: 'Intermediate',
        estimatedTime: '14 min read'
      },
      {
        id: 'eb2',
        title: 'Buy-Out Process',
        description: 'Complete guide to the buy-out process',
        content: 'Buy-out process: 1) Initial assessment, 2) Due diligence, 3) Negotiation, 4) Legal documentation, 5) Transition planning, 6) Employee communication, 7) Final transfer.',
        tags: ['buy-out', 'transition', 'process'],
        difficulty: 'Advanced',
        estimatedTime: '25 min read'
      },
      {
        id: 'eb3',
        title: 'Post-Hiring Management',
        description: 'What happens after you hire your talent',
        content: 'Post-hiring includes: Onboarding process, Performance management, Ongoing support, Career development, Retention strategies, and long-term relationship building.',
        tags: ['onboarding', 'management', 'retention'],
        difficulty: 'Intermediate',
        estimatedTime: '16 min read'
      }
    ],
    'Plans & Billing': [
      {
        id: 'pb1',
        title: 'Subscription Plans',
        description: 'Compare and choose the right plan for your needs',
        content: 'Plan comparison: Free vs Basic vs Premium vs Prestige, Feature breakdown, Pricing tiers, Plan benefits, Upgrade options, and custom enterprise solutions.',
        tags: ['plans', 'pricing', 'subscription'],
        difficulty: 'Beginner',
        estimatedTime: '9 min read'
      },
      {
        id: 'pb2',
        title: 'Billing & Invoices',
        description: 'Manage your billing and access invoices',
        content: 'Billing management: Payment methods, Invoice generation, Payment history, Tax calculations, Expense tracking, and financial reporting tools.',
        tags: ['billing', 'invoices', 'payments'],
        difficulty: 'Beginner',
        estimatedTime: '7 min read'
      },
      {
        id: 'pb3',
        title: 'Add-ons & Upgrades',
        description: 'Enhance your plan with additional services',
        content: 'Available add-ons: Additional credits, Role passes, Premium support, Custom integrations, White-label options, and dedicated account management.',
        tags: ['add-ons', 'upgrades', 'services'],
        difficulty: 'Intermediate',
        estimatedTime: '8 min read'
      }
    ],
    'FAQs': [
      {
        id: 'faq1',
        title: 'How do I get started?',
        description: 'Quick start guide for new users',
        content: 'Getting started is easy: 1) Sign up for an account, 2) Complete your profile, 3) Verify your account, 4) Start searching for talent, 5) Make your first hire.',
        tags: ['getting-started', 'new-user', 'signup'],
        difficulty: 'Beginner',
        estimatedTime: '3 min read'
      },
      {
        id: 'faq2',
        title: 'What are credits and how do they work?',
        description: 'Understanding the credit system',
        content: 'Credits are used for: Talent assessments, Interviews, Skill evaluations, and platform features. 1 credit = 1 assessment. Purchase additional credits as needed.',
        tags: ['credits', 'system', 'assessment'],
        difficulty: 'Beginner',
        estimatedTime: '4 min read'
      },
      {
        id: 'faq3',
        title: 'How do I manage my team?',
        description: 'Team management and permissions',
        content: 'Team management includes: Adding team members, Setting permissions, Role assignments, Access control, Collaboration tools, and performance monitoring.',
        tags: ['team', 'management', 'permissions'],
        difficulty: 'Intermediate',
        estimatedTime: '6 min read'
      },
      {
        id: 'faq4',
        title: 'What payment methods do you accept?',
        description: 'Payment options and security',
        content: 'We accept: Credit cards, Debit cards, Bank transfers, Digital wallets, and corporate accounts. All payments are secure and encrypted.',
        tags: ['payment', 'security', 'methods'],
        difficulty: 'Beginner',
        estimatedTime: '3 min read'
      }
    ]
  };

  const categories = Object.keys(knowledgeBase);
  const allItems = Object.values(knowledgeBase).flat();

  // Filter items based on search and category
  const filteredItems = allItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || 
                           knowledgeBase[selectedCategory]?.some(catItem => catItem.id === item.id);
    return matchesSearch && matchesCategory;
  });

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Beginner': '#00ff88',
      'Intermediate': '#ffbb33',
      'Advanced': '#ff6b6b'
    };
    return colors[difficulty] || '#cccccc';
  };

  return (
    <div className="knowledge-hub-page">
      <Header />
      
      <main className="knowledge-main">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">Knowledge Hub</h1>
            <p className="hero-subtitle">All the help you need, all in one place</p>
            <p className="hero-description">
              Your comprehensive self-service library built to answer FAQs, guide onboarding, 
              and support everyday platform usage.
            </p>
          </div>
          <div className="hero-visual">
            <div className="floating-icons">
              <span className="floating-icon">📚</span>
              <span className="floating-icon">💡</span>
              <span className="floating-icon">🔍</span>
              <span className="floating-icon">🚀</span>
            </div>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="search-section">
          <div className="search-container">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search knowledge base..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-container">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-filter"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Categories Overview */}
        <section className="categories-section">
          <h2 className="section-title">Browse by Category</h2>
          <div className="categories-grid">
            {categories.map(category => (
              <div 
                key={category}
                className={`category-card ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                <div className="category-icon">
                  {getCategoryIcon(category)}
                </div>
                <h3 className="category-name">{category}</h3>
                <p className="category-description">{getCategoryDescription(category)}</p>
                <span className="article-count">{knowledgeBase[category].length} articles</span>
              </div>
            ))}
          </div>
        </section>

        {/* Search Results */}
        <section className="results-section">
          <div className="results-header">
            <h2 className="section-title">
              {searchTerm ? `Search Results for "${searchTerm}"` : 'All Articles'}
            </h2>
            <span className="results-count">{filteredItems.length} articles found</span>
          </div>
          
          <div className="articles-grid">
            {filteredItems.map(item => (
              <div key={item.id} className="article-card">
                <div className="article-header">
                  <div className="article-meta">
                    <span 
                      className="difficulty-badge"
                      style={{ backgroundColor: getDifficultyColor(item.difficulty) }}
                    >
                      {item.difficulty}
                    </span>
                    <span className="read-time">{item.estimatedTime}</span>
                  </div>
                  <button 
                    className={`expand-btn ${expandedItems[item.id] ? 'expanded' : ''}`}
                    onClick={() => toggleExpanded(item.id)}
                  >
                    {expandedItems[item.id] ? '−' : '+'}
                  </button>
                </div>
                
                <div className="article-content">
                  <h3 className="article-title">{item.title}</h3>
                  <p className="article-description">{item.description}</p>
                  
                  {expandedItems[item.id] && (
                    <div className="article-details">
                      <div className="article-full-content">{item.content}</div>
                      <div className="article-tags">
                        {item.tags.map(tag => (
                          <span key={tag} className="tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

// Helper function to get category icons
function getCategoryIcon(category) {
  const icons = {
    'Getting Started': '🚀',
    'Using the Portal': '💻',
    'Credits & Assessment': '🎫',
    'Documents & Contracts': '📋',
    'EOR & Buy-Out Guide': '🤝',
    'Plans & Billing': '💳',
    'FAQs': '❓'
  };
  return icons[category] || '📚';
}

// Helper function to get category descriptions
function getCategoryDescription(category) {
  const descriptions = {
    'Getting Started': 'Step by Step Guide',
    'Using the Portal': 'Tutorials on using the portal',
    'Credits & Assessment': 'How to use credits, role pass',
    'Documents & Contracts': 'Managing signed agreements',
    'EOR & Buy-Out Guide': 'What happens after hiring',
    'Plans & Billing': 'Manage plans, invoices',
    'FAQs': 'Quick answers to common queries'
  };
  return descriptions[category] || 'Knowledge base articles';
}

export default KnowledgeHubPage;
